/**
 * Stale-tab guard (Oct 7 2026). A tab opened before a deploy keeps running the
 * old JavaScript, including already-loaded pages like the session screen, until
 * a full reload. Sam and Lauren joined the 2:47 PM call in tabs still on the
 * 2:15 PM build, so the new in-call guideline notice never ran.
 *
 * We compare our own main bundle with the server's. When a newer build is out,
 * the NEXT page change does a full load, but never while a call is open.
 */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const APP_VERSION_POLL_MS = 5 * 60 * 1000;
const RELOADED_FOR_KEY = 'sl.appVersionReloadedFor';

/** "main.aa98069d.js" from this page's own script tag, or null. */
export const ownMainBundle = (doc = typeof document !== 'undefined' ? document : null) => {
  if (!doc) {
    return null;
  }
  const scripts = Array.from(doc.querySelectorAll('script[src]'));
  const match = scripts
    .map(el => el.getAttribute('src') || '')
    .map(src => (src.match(/\/static\/js\/(main\.[a-z0-9]+\.js)/i) || [])[1])
    .find(Boolean);
  return match || null;
};

/** True while a talk session is open on screen (never reload mid-call). */
export const isSessionOpenOnScreen = (doc = typeof document !== 'undefined' ? document : null) =>
  !!doc && !!doc.querySelector('[data-session-open="true"]');

/**
 * @returns {boolean} true when the server has a different main bundle than ours
 */
export const isStaleBuild = ({ own, server }) => !!own && !!server && own !== server;

/**
 * Should this page change turn into a full load?
 * Skips when a call is open, or when we already reloaded once for that build
 * (a cached old page must not loop).
 */
export const shouldReloadOnNavigate = ({ own, server, sessionOpen, reloadedFor, pathname = '' }) =>
  isStaleBuild({ own, server }) &&
  !sessionOpen &&
  reloadedFor !== server &&
  // Checkout keeps its draft in memory + session storage; leave it alone.
  !/\/checkout(\/|$)/.test(pathname || '');

const fetchServerMain = () =>
  typeof window === 'undefined' || typeof window.fetch !== 'function'
    ? Promise.resolve(null)
    : window
        .fetch('/api/app-version', { credentials: 'same-origin', cache: 'no-store' })
        .then(res => (res.ok ? res.json() : null))
        .then(body => (body && body.data && body.data.main) || null)
        .catch(() => null);

const readReloadedFor = () => {
  try {
    return window.sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch (e) {
    return null;
  }
};
const writeReloadedFor = value => {
  try {
    window.sessionStorage.setItem(RELOADED_FOR_KEY, value);
  } catch (e) {
    // private mode: fine
  }
};

export const AppVersionGuard = () => {
  const location = useLocation();
  const serverMainRef = useRef(null);
  const firstPathRef = useRef(true);

  useEffect(() => {
    if (typeof window === 'undefined' || !ownMainBundle()) {
      return undefined;
    }
    let active = true;
    const check = () =>
      fetchServerMain().then(main => {
        if (active && main) {
          serverMainRef.current = main;
        }
      });
    check();
    const timer = window.setInterval(check, APP_VERSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    if (firstPathRef.current) {
      firstPathRef.current = false;
      return;
    }
    const server = serverMainRef.current;
    if (
      shouldReloadOnNavigate({
        own: ownMainBundle(),
        server,
        sessionOpen: isSessionOpenOnScreen(),
        reloadedFor: readReloadedFor(),
        pathname: location.pathname,
      })
    ) {
      writeReloadedFor(server);
      window.location.reload();
    }
  }, [location.pathname]);

  return null;
};

export default AppVersionGuard;
